import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import {
  ALLOWED_DOMAINS_DISPLAY,
  isValidRedirectUrl,
  isSameUrl,
} from "./redirectConfig";

const PLACEHOLDER = `https://obenelectric.com/old-page, https://obenelectric.com/new-page
https://obenev.obenelectric.com/press-release, https://obenelectric.com/news
https://uicw.obenev.obenelectric.com/buy-rorr, https://obenelectric.com/buy-now`;

export default function BulkPasteModal({ open, onClose, onSave, loading }) {
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState(null);
  const [defaultStatusCode, setDefaultStatusCode] = useState("none");
  const [parseError, setParseError] = useState("");

  const handleParse = () => {
    setParseError("");

    if (defaultStatusCode === "none") {
      setParseError("Please select a redirect type (301 or 302) before previewing.");
      return;
    }

    const lines = text
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    if (!lines.length) {
      setParseError("Paste at least one line.");
      return;
    }

    const valid = [];
    const invalid = [];

    lines.forEach((line, i) => {
      const sep = line.includes(",") ? "," : line.includes("\t") ? "\t" : " ";
      const parts = line.split(sep).map((p) => p.trim()).filter(Boolean);

      if (parts.length < 2) {
        invalid.push({ line: i + 1, raw: line, reason: "Missing destination" });
        return;
      }

      const from_raw = parts[0];
      const to_url = parts[1];

      if (!isValidRedirectUrl(from_raw)) {
        invalid.push({
          line: i + 1,
          raw: line,
          reason: `"From" must use an allowed domain: ${ALLOWED_DOMAINS_DISPLAY}`,
        });
        return;
      }

      if (!isValidRedirectUrl(to_url)) {
        invalid.push({
          line: i + 1,
          raw: line,
          reason: `"To" must use an allowed domain: ${ALLOWED_DOMAINS_DISPLAY}`,
        });
        return;
      }

      if (isSameUrl(from_raw, to_url)) {
        invalid.push({
          line: i + 1,
          raw: line,
          reason: '"From" and "To" URLs cannot be the same',
        });
        return;
      }

      valid.push({
        from_path: from_raw,
        to_url: to_url,
        status_code: Number(defaultStatusCode),
        is_active: true,
      });
    });

    setParsed({ valid, invalid });
  };

  const handleReset = () => {
    setParsed(null);
    setParseError("");
  };

  const handleFullReset = () => {
    setText("");
    setParsed(null);
    setParseError("");
    setDefaultStatusCode("none");
  };

  const handleConfirm = async () => {
    if (parsed?.valid?.length) {
      await onSave(parsed.valid);
      handleFullReset();
    }
  };

  const handleClose = () => {
    handleFullReset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          fontWeight: 600,
          fontSize: "1rem",
        }}
      >
        Paste bulk redirects
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {!parsed ? (
          <>
            <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
              One redirect per line. Both URLs must include an allowed domain.
              <br />
              <strong>Allowed domains:</strong> {ALLOWED_DOMAINS_DISPLAY}
              <br />
              <strong>Example:</strong>{" "}
              <code>
                https://obenelectric.com/old-page, https://obenelectric.com/new-page
              </code>
            </Alert>

            <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "flex-start" }}>
              <FormControl
                size="small"
                sx={{ minWidth: 220 }}
                error={!!parseError && defaultStatusCode === "none"}
              >
                <InputLabel>Redirect type *</InputLabel>
                <Select
                  value={defaultStatusCode}
                  onChange={(e) => {
                    setDefaultStatusCode(e.target.value);
                    if (parseError) setParseError("");
                  }}
                  label="Redirect type *"
                >
                  <MenuItem value="none" disabled>
                    Select type
                  </MenuItem>
                  <MenuItem value={301}>301 — Permanent</MenuItem>
                  <MenuItem value={302}>302 — Temporary</MenuItem>
                </Select>
                {parseError && defaultStatusCode === "none" && (
                  <Typography fontSize={11} color="error" mt={0.5}>
                    Redirect type is required
                  </Typography>
                )}
              </FormControl>
            </Box>

            <TextField
              multiline
              rows={10}
              fullWidth
              placeholder={PLACEHOLDER}
              value={text}
              onChange={(e) => setText(e.target.value)}
              error={!!parseError && defaultStatusCode !== "none"}
              helperText={
                parseError && defaultStatusCode !== "none" ? parseError : ""
              }
              inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
              sx={{ mb: 1 }}
            />
            <Typography fontSize={12} color="text.secondary">
              Both columns must use one of the allowed domains. From and To URLs
              cannot be the same.
            </Typography>
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${parsed.valid.length} valid`}
                color="success"
                variant="outlined"
                size="small"
              />
              {parsed.invalid.length > 0 && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`${parsed.invalid.length} skipped`}
                  color="error"
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>

            {parsed.valid.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography fontSize={13} fontWeight={600} mb={1} color="text.secondary">
                  WILL BE ADDED
                </Typography>
                <Box
                  sx={{
                    maxHeight: 220,
                    overflowY: "auto",
                    border: "1px solid #e8f5e9",
                    borderRadius: 1.5,
                    backgroundColor: "#f9fbe7",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>From</TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>To</TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsed.valid.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell
                            sx={{
                              fontFamily: "monospace",
                              fontSize: 12,
                              color: "#1565c0",
                              maxWidth: 220,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={r.from_path}
                          >
                            {r.from_path}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "monospace",
                              fontSize: 12,
                              color: "#2e7d32",
                              maxWidth: 220,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={r.to_url}
                          >
                            {r.to_url}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {r.status_code ?? "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}

            {parsed.invalid.length > 0 && (
              <Box>
                <Typography fontSize={13} fontWeight={600} mb={1} color="error.main">
                  SKIPPED (invalid)
                </Typography>
                <Box
                  sx={{
                    border: "1px solid #ffebee",
                    borderRadius: 1.5,
                    backgroundColor: "#fff8f8",
                    p: 1.5,
                    maxHeight: 200,
                    overflowY: "auto",
                  }}
                >
                  {parsed.invalid.map((err, i) => (
                    <Box key={i} sx={{ mb: 0.5 }}>
                      <Typography fontSize={12}>
                        <strong>Line {err.line}:</strong>{" "}
                        <code style={{ color: "#c62828" }}>{err.raw}</code> —{" "}
                        {err.reason}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        {parsed ? (
          <>
            <Button
              onClick={handleReset}
              variant="outlined"
              sx={{ textTransform: "none", borderColor: "#bdbdbd", color: "#424242" }}
            >
              Back
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              disabled={loading || !parsed.valid.length}
              sx={{
                textTransform: "none",
                backgroundColor: "#1976d2",
                "&:hover": { backgroundColor: "#1565c0" },
                minWidth: 120,
              }}
            >
              {loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                `Add ${parsed.valid.length} redirect${parsed.valid.length !== 1 ? "s" : ""}`
              )}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ textTransform: "none", borderColor: "#bdbdbd", color: "#424242" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleParse}
              variant="contained"
              disabled={!text.trim()}
              sx={{
                textTransform: "none",
                backgroundColor: "#1976d2",
                "&:hover": { backgroundColor: "#1565c0" },
              }}
            >
              Preview
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}